import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { isGlobalAdmin } from '../services/licenses';
import './Navigation.css';

const Navigation: React.FC = () => {
  const { user, player, signOut, isProfileComplete } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Close menu when location changes
  useEffect(() => {
    setShowMenu(false);
  }, [location]);

  // Check global admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const adminStatus = await isGlobalAdmin();
        setIsAdmin(adminStatus);
      } catch (err) {
        console.error('Failed to check admin status:', err);
        setIsAdmin(false);
      }
    };

    if (user) {
      checkAdminStatus();
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <nav className="navigation">
      <div className="nav-container">
        <Link to="/games" className="nav-brand">
          Snooker App
        </Link>
        
        <div className="nav-menu">
          <Link to="/games" className="nav-link">{t('navigation.games')}</Link>
          <Link to="/clubs" className="nav-link">{t('clubs.title')}</Link>
          <Link to="/training" className="nav-link">{t('navigation.training')}</Link>
          
          {/* Profile Completion Indicator */}
          {!isProfileComplete && player && (
            <Link to="/profile" className="nav-link nav-profile-warning">
              ⚠️ {t('navigation.completeProfile')}
            </Link>
          )}
          
          <div className="nav-user">
            <button 
              className="nav-user-button"
              onClick={() => setShowMenu(!showMenu)}
            >
              <div className="nav-user-info">
                {player?.profile_picture_url ? (
                  <img 
                    src={player.profile_picture_url} 
                    alt="Profile" 
                    className="nav-user-avatar"
                  />
                ) : (
                  <div className="nav-user-avatar nav-user-avatar-placeholder">
                    {player ? `${player.preName?.charAt(0) || ''}${player.name?.charAt(0) || ''}` : '?'}
                  </div>
                )}
                <span className="nav-user-name">
                  {player ? `${player.preName || ''} ${player.name || ''}`.trim() || user?.email : user?.email}
                </span>
              </div>
            </button>
            
            {showMenu && (
              <div className="nav-dropdown">
                <Link to="/profile" className="nav-dropdown-item">
                  {t('navigation.profile')}
                </Link>
                <Link to="/statistics" className="nav-dropdown-item">
                  {t('navigation.statistics')}
                </Link>
                <Link to="/invitations" className="nav-dropdown-item">
                  {t('navigation.invitations')}
                </Link>
                {isAdmin && (
                  <Link to="/global-admin" className="nav-dropdown-item nav-admin-dropdown-item">
                    🌐 {t('navigation.globalAdmin')}
                  </Link>
                )}
                <div className="nav-dropdown-divider"></div>
                <button onClick={handleSignOut} className="nav-dropdown-item">
                  {t('navigation.signOut')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;

